"use client"

import { useState } from "react"
import { Meeting } from "@/schema"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ui/shad"
import { AudioLines, Download, FileText, Video } from "lucide-react"
import { toast } from "sonner"

import { useDownloadMeetingAudio, useDownloadMeetingTranscript, useDownloadMeetingVideo } from "@/hooks/api/meetings"

interface MeetingDownloadDropdownProps {
  meeting?: Meeting
  hideRecordingDownloads?: boolean
  hideTranscriptDownload?: boolean
}

export default function MeetingDownloadDropdown({
  meeting,
  hideRecordingDownloads = false,
  hideTranscriptDownload = false,
}: MeetingDownloadDropdownProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const downloadTranscript = useDownloadMeetingTranscript()
  const downloadVideo = useDownloadMeetingVideo()
  const downloadAudio = useDownloadMeetingAudio()

  const hasVideo = Boolean(meeting?.mux_video?.playback_id) && !hideRecordingDownloads
  const hasTranscript = Boolean(
    (meeting?.transcript && meeting.transcript.length > 0) || meeting?.user_uploaded_transcript
  )
  const canDownloadTranscript = hasTranscript && !hideTranscriptDownload
  const hasUploadedAudio = Boolean(meeting?.primary_recording) && !hideRecordingDownloads
  const hasAudio = hasVideo || hasUploadedAudio
  const hasAnyDownloads = canDownloadTranscript || hasVideo || hasUploadedAudio

  const handleDownloadTranscript = async () => {
    if (!meeting?.id || !canDownloadTranscript) return

    setIsDownloading(true)
    try {
      await downloadTranscript(meeting.id, meeting.title)
      toast.success("Transcript downloaded successfully")
    } catch (error) {
      console.error("Error downloading transcript:", error)
      toast.error("Failed to download transcript")
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDownloadVideo = async () => {
    if (!meeting?.id || !hasVideo) return

    setIsDownloading(true)
    try {
      await downloadVideo(meeting.id)
      toast.success("Downloading video...")
    } catch (error) {
      console.error("Error downloading video:", error)
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error("Failed to download video")
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDownloadAudio = async () => {
    if (!meeting?.id || !hasAudio) return

    setIsDownloading(true)
    try {
      await downloadAudio(meeting.id)
      toast.success("Downloading audio...")
    } catch (error) {
      console.error("Error downloading audio:", error)
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error("Failed to download audio")
      }
    } finally {
      setIsDownloading(false)
    }
  }

  if (!hasAnyDownloads) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="iconSm" disabled={isDownloading}>
          <Download className="text-icon" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canDownloadTranscript && (
          <DropdownMenuItem onClick={handleDownloadTranscript} disabled={isDownloading}>
            <FileText />
            Download transcript
          </DropdownMenuItem>
        )}
        {canDownloadTranscript && (hasVideo || hasAudio) && <DropdownMenuSeparator />}
        {hasVideo && (
          <DropdownMenuItem onClick={handleDownloadVideo} disabled={isDownloading}>
            <Video />
            Download video
          </DropdownMenuItem>
        )}
        {hasAudio && (
          <DropdownMenuItem onClick={handleDownloadAudio} disabled={isDownloading}>
            <AudioLines />
            Download audio
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
